import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import {
  BREACH_ACKNOWLEDGEMENT_COUNT_CHANGED_EVENT,
  breachAcknowledgementCountQueryKey,
  getBreachAcknowledgementCount,
} from '@/lib/breachAcknowledgements';
import { listCustomerBreachNotices } from '@/lib/breachNotifications';
import { FunctionInvokeError } from '@/lib/functionInvokeErrors';
import { queryRetryPolicy } from '@/lib/transientBackendError';

/**
 * ENT-6330: the badge polls only on the Breach Centre, the screen where a customer triages and
 * acknowledges notices. Every other screen reads the count on mount, on tab focus, on navigation
 * (once stale) and on an acknowledgement event, never on a cadence. An idle `/pitch-book` tab
 * otherwise repeated `breach-notification-cases` every ~31 s (ENT-5462 AC6, staging
 * v1.3.49+sha.6b26f31). The browser-validation collector declares this reader as the
 * `breach-acknowledgement` bounded poll (scripts/lib/browser-validation.mjs).
 */
export const BREACH_ACKNOWLEDGEMENT_POLL_PATH = '/breach-notifications';

/**
 * 30 s on the Breach Centre: a supplier notice that lands while the user is triaging appears
 * within one interval, against notice windows measured in hours (24 h / 72 h). Twice the stale
 * time, so a focus or navigation refresh never doubles a tick; the E2E cadence allowance
 * (e2e/lib/sso-cadence.ts) relies on ticks at least 15 s apart.
 */
export const BREACH_COUNT_POLL_INTERVAL_MS = 30_000;
const BREACH_COUNT_STALE_TIME_MS = 15_000;

/**
 * ENT-4778: a 401 (revoked/unadmitted session) or 403 (e.g. `subscription_inactive`
 * entitlement denial) is a terminal answer for this session — retrying or polling
 * it cannot succeed and produced a sustained request storm on staging (the SSO
 * lane's repeating-cadence guard). Transient failures keep the app retry policy.
 */
function isTerminalAuthorizationError(error: unknown): boolean {
  return error instanceof FunctionInvokeError && (error.status === 401 || error.status === 403);
}

function isBreachCentrePath(pathname: string): boolean {
  return pathname === BREACH_ACKNOWLEDGEMENT_POLL_PATH
    || pathname.startsWith(`${BREACH_ACKNOWLEDGEMENT_POLL_PATH}/`);
}

export function useBreachAcknowledgementCount({
  organizationId,
  enabled,
}: {
  organizationId: string | null | undefined;
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const isOnBreachCentre = isBreachCentrePath(pathname);
  const queryKey = breachAcknowledgementCountQueryKey(organizationId);
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!organizationId) return 0;
      const result = await listCustomerBreachNotices({ organizationId, signal });
      return getBreachAcknowledgementCount(result.cases);
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: BREACH_COUNT_STALE_TIME_MS,
    retry: (failureCount, error) => !isTerminalAuthorizationError(error) && queryRetryPolicy(failureCount, error),
    refetchInterval: (query) => (
      isOnBreachCentre && !isTerminalAuthorizationError(query.state.error)
        ? BREACH_COUNT_POLL_INTERVAL_MS
        : false
    ),
    // Visible-tab only: a hidden tab never polls.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) => !isTerminalAuthorizationError(query.state.error),
  });

  // A navigation between screens refreshes a stale count; the first render's mount read
  // already covers the landing screen, and a query-string change is not a navigation.
  const previousPathnameRef = useRef(pathname);
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (!enabled || !organizationId) return;
    const state = queryClient.getQueryState(breachAcknowledgementCountQueryKey(organizationId));
    if (!state || isTerminalAuthorizationError(state.error)) return;
    if (state.fetchStatus === 'fetching') return;
    if (Date.now() - state.dataUpdatedAt < BREACH_COUNT_STALE_TIME_MS) return;
    void queryClient.refetchQueries({
      exact: true,
      queryKey: breachAcknowledgementCountQueryKey(organizationId),
      type: 'active',
    });
  }, [enabled, organizationId, pathname, queryClient]);

  useEffect(() => {
    if (!enabled || !organizationId) return undefined;

    const refreshCount = () => {
      void query.refetch();
    };
    const cancelInFlightCount = () => {
      void queryClient.cancelQueries({
        exact: true,
        queryKey: breachAcknowledgementCountQueryKey(organizationId),
      });
    };
    window.addEventListener(BREACH_ACKNOWLEDGEMENT_COUNT_CHANGED_EVENT, refreshCount);
    window.addEventListener('pagehide', cancelInFlightCount);
    return () => {
      window.removeEventListener(BREACH_ACKNOWLEDGEMENT_COUNT_CHANGED_EVENT, refreshCount);
      window.removeEventListener('pagehide', cancelInFlightCount);
    };
  }, [enabled, organizationId, query.refetch, queryClient]);

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
  };
}
